import { useEffect, useState } from "react";
import { useConnectors as _useConnectors } from "wagmi";

// Define EIP-6963 interface types
interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
  provider: any;
}

interface EIP6963AnnounceProviderEvent extends Event {
  detail: {
    info: EIP6963ProviderInfo;
    provider: any;
  };
}

// Helper function to get random items from array
const getRandomItems = (array: any[], count: number) => {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

export const getPopularWallets = (connectors: readonly any[]) => {
  // Always include WalletConnect
  const walletConnect = connectors.find(c => c.id === "walletConnect");

  // Get installed wallets (excluding WalletConnect)
  const installedWallets = connectors.filter(c => c.isInstalled && c.id !== "walletConnect");

  // Get uninstalled wallets (excluding WalletConnect)
  const uninstalledWallets = connectors.filter(c => !c.isInstalled && c.id !== "walletConnect");

  const popularWallets: any[] = [];

  // Add WalletConnect first
  if (walletConnect) {
    popularWallets.push(walletConnect);
  }

  // Add up to 2 installed wallets
  if (installedWallets.length > 0) {
    popularWallets.push(...installedWallets.slice(0, 2));
  }

  // If we have less than 3 wallets total, add the first few uninstalled ones
  const remaining = 3 - popularWallets.length;
  if (remaining > 0 && uninstalledWallets.length > 0) {
    popularWallets.push(...uninstalledWallets.slice(0, remaining));
  }

  return popularWallets.slice(0, 3);
};

export default function useConnectors() {
  const _connectors = _useConnectors();
  const [connectors, setConnectors] = useState(_connectors);
  const [eip6963Providers, setEip6963Providers] = useState<EIP6963ProviderInfo[]>([]);

  // Handle EIP-6963 wallet announcements
  useEffect(() => {
    const providersMap = new Map<string, EIP6963ProviderInfo>();

    const handleAnnouncement = (event: EIP6963AnnounceProviderEvent) => {
      const { info } = event.detail;
      providersMap.set(info.uuid, info);
      setEip6963Providers(Array.from(providersMap.values()));
    };

    // Listen for wallet announcements
    window.addEventListener("eip6963:announceProvider", handleAnnouncement as EventListener);

    // Request wallets to announce themselves
    window.dispatchEvent(new Event("eip6963:requestProvider"));

    return () => {
      window.removeEventListener("eip6963:announceProvider", handleAnnouncement as EventListener);
    };
  }, []);

  useEffect(() => {
    const loadIcons = async () => {
      const updated = await Promise.all(
        _connectors.map(async c => {
          // Check if this wallet is announced via EIP-6963
          const matchingEip6963Provider = eip6963Providers.find(
            provider => provider.name.toLowerCase() === c.name?.toLowerCase(),
          );

          const isInstalled = !!matchingEip6963Provider || false;

          if (c.id === "metaMask" || c.id === "walletConnect" || c.id == "injected" || c.id === "ID_AUTH") return;
          // if (c.id === "metaMask") return;

          const newC = {
            isInstalled,
            ...c,
            name: c.name,
            id: c.id,
            icon:
              c.id === "coinbaseWalletSDK" || c.id === "baseAccount"
                ? "/images/network-logos/base.svg"
                : c.icon
                ? c.icon.trim()
                : "/images/logos/WalletConnectLogo.svg",
          };

          return newC;
        }),
      );
      const filtered = updated
        .filter(c => c !== undefined)
        .toSorted((a, b) => {
          if (a?.id === "baseAccount") return -1;
          if (b?.id === "baseAccount") return 1;
          if (a?.isInstalled && !b?.isInstalled) return -1;
          if (!a?.isInstalled && b?.isInstalled) return 1;
          return 0;
        });
      setConnectors(filtered as any);
    };

    loadIcons();
  }, [_connectors, eip6963Providers]);

  return connectors;
}
